import { useId } from 'react';

/**
 * Nexus brand mark: an N-monogram (purple left / silver right, two-tone diagonal)
 * inside a circuit ring with node connectors. Rendered inline as SVG so it stays
 * crisp at any size/DPI. Mirrors web/src/components/NexusMark.tsx (product app)
 * so the marketing site and the app render an identical mark.
 */
export function NexusMark({ size = 24, title = 'Nexus' }: { size?: number; title?: string }): JSX.Element {
  const uid = useId().replace(/[:]/g, '');
  const g = (name: string) => `nx-${uid}-${name}`;
  return (
    <svg width={size} height={size} viewBox="0 0 256 256" role="img" aria-label={title}
      xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={g('ring')} x1="18" y1="26" x2="238" y2="230" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#b06bff" />
          <stop offset="0.5" stopColor="#8b3df0" />
          <stop offset="1" stopColor="#6d28d9" />
        </linearGradient>
        <linearGradient id={g('purple')} x1="0" y1="70" x2="0" y2="188" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#c489ff" />
          <stop offset="0.55" stopColor="#8b3df0" />
          <stop offset="1" stopColor="#5b1fb8" />
        </linearGradient>
        <linearGradient id={g('silver')} x1="0" y1="70" x2="0" y2="188" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#ffffff" />
          <stop offset="0.5" stopColor="#d3dae6" />
          <stop offset="1" stopColor="#9aa6ba" />
        </linearGradient>
        <linearGradient id={g('diag')} x1="106" y1="74" x2="150" y2="182" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#a855f7" />
          <stop offset="0.45" stopColor="#8b5cf6" />
          <stop offset="0.78" stopColor="#d3dae6" />
          <stop offset="1" stopColor="#ffffff" />
        </linearGradient>
      </defs>
      <g fill="none" stroke={`url(#${g('ring')})`} strokeLinecap="round">
        <circle cx="128" cy="128" r="103" strokeWidth="7" />
        <line x1="41" y1="118" x2="26" y2="118" strokeWidth="6" />
        <circle cx="16" cy="118" r="7.5" strokeWidth="5" />
        <line x1="215" y1="140" x2="230" y2="140" strokeWidth="6" />
        <circle cx="240" cy="140" r="7.5" strokeWidth="5" />
      </g>
      <g transform="translate(6,7)" fill="#3a1670">
        <polygon points="84,74 106,74 106,182 84,182" />
        <polygon points="106,74 128,74 150,182 128,182" />
        <polygon points="150,74 172,74 172,182 150,182" />
      </g>
      <polygon points="84,74 106,74 106,182 84,182" fill={`url(#${g('purple')})`} />
      <polygon points="106,74 128,74 150,182 128,182" fill={`url(#${g('diag')})`} />
      <polygon points="150,74 172,74 172,182 150,182" fill={`url(#${g('silver')})`} />
      <g opacity="0.55" fill="#ffffff">
        <polygon points="84,74 106,74 106,79 84,79" />
        <polygon points="150,74 172,74 172,79 150,79" />
      </g>
    </svg>
  );
}
